/**
 * AI Storybook — client for /api/web/storybook.
 *
 * Deliberately separate from src/lib/api.ts: the storybook is a children's
 * product walled off from the rest of the site. It has no credits and no
 * account requirement; each book is opened with its own access token
 * (X-Storybook-Token), which arrives either from POST /books or from the
 * private link in the parent's email (/storybook/<id>?k=<token>).
 *
 * Nothing in this module sends anything to analytics. The child's name, age,
 * appearance, interests, story text and photos travel ONLY to our own backend.
 * A parent may add up to six photos of the child and of the family members in
 * the story (never of a friend: config.photoRelations) and mark who each face
 * is; the server keeps only those face crops, uses them only to draw this
 * book, and deletes them once the parent approves the pictures, or when they
 * expire (docs/storybook-identity-lock.md §2, §8, §9.4). Without photos
 * everyone is drawn from the appearance choices.
 *
 * A paid book is never made straight away: once every picture is drawn it
 * waits in `proofing` until the parent has checked the faces and approved it,
 * or asked for some pictures to be drawn again (§9).
 */

import { resolveBackendBase } from '@/lib/resolve-backend-base';

const BACKEND_BASE = resolveBackendBase(
  (import.meta as unknown as { env?: Record<string, unknown> }).env,
);

const API_ROOT = `${BACKEND_BASE}/api/web/storybook`;

// ---------------------------------------------------------------------------
// Types — mirror the contract in the storybook spec.
// ---------------------------------------------------------------------------

export type StorybookLanguage = 'en' | 'es';
export type StorybookGender = 'boy' | 'girl' | 'child';
/** The cast: the child (hero), then companions[0] (c1) and companions[1] (c2). */
export type StorybookPersonId = 'hero' | 'c1' | 'c2';
/** How the latest cover drew a person: from their photo, from the description, or a photo it could not read. */
export type StorybookLikeness = 'photo' | 'description' | 'photo_unreadable';
/** A face: normalized [x0, y0, x1, y1] (0..1) in the pixels of the photo exactly as it is sent. */
export type StorybookFaceBox = [number, number, number, number];

export interface StorybookOption {
  id: string;
  en: string;
  es: string;
  emoji?: string;
}

export interface StorybookPricePoint {
  cents: number;
  usdCents: number;
}

export interface StorybookPrices {
  currency: string;
  pdf: StorybookPricePoint;
  print: StorybookPricePoint;
  printUpgrade: StorybookPricePoint;
  /** Charged on top of a printed book at checkout; absent or 0 = free shipping. */
  shipping?: StorybookPricePoint;
}

export interface StorybookAppearanceOptions {
  skinTone: string[];
  hairColor: string[];
  hairStyle: string[];
  eyeColor: string[];
}

export interface StorybookLimits {
  /** Largest photo accepted, PER PHOTO, in decoded bytes. */
  maxPhotoBytes?: number;
  /** Every photo of one request together, decoded bytes. */
  maxTotalPhotoBytes?: number;
  /** Photos per request; absent or 0 = this backend takes no photos (the builder hides the step). */
  maxPhotos?: number;
  /** Face crops kept per person (a person is tagged at most this many times). */
  maxRefsPerPerson?: number;
  /** A tagged face's box must be at least this many photo pixels on its shorter side. */
  minFacePx?: number;
  /** The long edge the browser resizes a photo to before sending it. */
  photoLongEdge?: number;
  nameMax: number;
  interestsMax: number;
  sidekickMax: number;
  dedicationMax: number;
  detailsMax: number;
  minAge: number;
  maxAge: number;
}

export interface StorybookConfig {
  enabled: boolean;
  printEnabled: boolean;
  prices: StorybookPrices;
  themes: StorybookOption[];
  lessons: StorybookOption[];
  artStyles: StorybookOption[];
  appearance: StorybookAppearanceOptions;
  genders: StorybookGender[];
  languages: StorybookLanguage[];
  /** Who a companion is to the child ('mom', 'big sister', …). */
  relations?: string[];
  /** The kinds a family pet may be ('dog', 'cat', …); absent on an older backend (no pet section). */
  petKinds?: string[];
  /** Relations whose companion may be photographed (every one but 'friend'); the child always may. */
  photoRelations?: string[];
  cast?: StorybookCastConfig;
  printCountries: string[];
  limits: StorybookLimits;
  /** Cover redraws (photo updates included) allowed before paying. */
  redraws?: { max: number };
  /** The consent wording's version, sent back with the book so the server records what was agreed to. */
  consentVersion?: string;
  /** A gift code: the book is created already paid by the gift. */
  giftCode?: string;
}

export interface StorybookCastConfig {
  maxCompanions: number;
  /** Relations drawn as children: they may carry an age (companionAge). */
  childRelations: string[];
  companionAge: { min: number; max: number };
}

export type StorybookStatus =
  | 'preview_generating'
  | 'preview_ready'
  | 'preview_failed'
  | 'generating'
  /** Every picture is drawn; nothing is made until the parent approves (§9.1). */
  | 'proofing'
  | 'assembling'
  | 'ready'
  | 'failed'
  | 'deleted';

export type StorybookPrintStatus =
  | 'pending'
  | 'submitting'
  | 'submitted'
  | 'accepted'
  | 'in_production'
  | 'shipped'
  | 'rejected'
  | 'canceled'
  | 'error';

export interface StorybookPage {
  index: number;
  text: string | null;
  imageUrl: string | null;
  locked: boolean;
}

export interface StorybookPrint {
  status: StorybookPrintStatus;
  carrier: string | null;
  trackingUrls: string[];
  country: string | null;
}

export interface StorybookOffer {
  available: boolean;
  cents: number;
  usdCents: number;
}

export interface StorybookPrintOffer extends StorybookOffer {
  kind: 'full' | 'upgrade';
}

/** One person of the book's cast (names are the parent's own text: never tracked). */
export interface StorybookCastMember {
  id: StorybookPersonId;
  name: string;
  relation?: string;
}

/** Why a picture should be drawn again (the face reasons redraw it with a stronger likeness). */
export type StorybookProofReason = 'face' | 'wrong_person' | 'other';

/**
 * The pictures the parent approves (serialized order, §9.3). In `proofing`
 * every field is there; while `generating` after a flag only `redrawing` is
 * relied on.
 */
export interface StorybookProof {
  /** 'cover', 'page01' … 'page10', in book order. */
  keys?: string[];
  /** Signed web-size pictures by key. */
  images?: Partial<Record<string, string | null>>;
  /** Each page's words by key (the cover has none). */
  texts?: Partial<Record<string, string>>;
  /** Redraw rounds still allowed. */
  roundsLeft?: number;
  maxRounds?: number;
  /** The keys being drawn again right now. */
  redrawing?: string[];
}

/** POST /books/:id/proof/redraw: 1..11 keys, in book order; a reason for every key ('face' where the parent picked none). */
export interface StorybookProofRedrawRequest {
  keys: string[];
  reasons: Partial<Record<string, StorybookProofReason>>;
}

export interface StorybookBook {
  id: string;
  status: StorybookStatus;
  createdAt: string;
  language: StorybookLanguage;
  childName: string;
  title: string | null;
  blurb: string | null;
  themeId: string;
  artStyleId: string;
  coverUrl: string | null;
  pages: StorybookPage[];
  progress: { done: number; total: number } | null;
  purchased: { pdf: boolean; print: boolean };
  downloads: { pdfUrl: string | null };
  print: StorybookPrint | null;
  offers: { pdf: StorybookOffer | null; print: StorybookPrintOffer | null } | null;
  retryable: boolean;
  error: { code: string; message: string } | null;
  cast?: StorybookCastMember[];
  /** Per person, from the latest cover's identity report; null until a cover exists. */
  likeness?: Partial<Record<StorybookPersonId, StorybookLikeness>> | null;
  /** The face crops are still stored (a redraw can still use them). */
  photosKept?: boolean;
  /** Redrawing the cover or replacing photos: allowed only on an unpaid preview. */
  redraw?: { allowed: boolean; left: number } | null;
  /** The pictures to approve (`proofing`), or the ones being drawn again (`generating`). */
  proof?: StorybookProof | null;
  /** Who gave this book, when a gift paid for it. */
  giftFrom?: string | null;
}

export interface StorybookAppearance {
  skinTone: string;
  hairColor: string;
  hairStyle: string;
  eyeColor: string;
  glasses: boolean;
  details: string;
}

/** A companion who joins the story: a grown-up, a big sibling or a friend (c1, c2 in this order). */
export interface StorybookCompanion {
  name: string;
  relation: string;
  /** Only for a child relation (config.cast.childRelations), companionAge.min..max. */
  age?: number;
  /** Only for a friend (a family relation already says it). */
  gender?: StorybookGender;
  /** Every appearance field is optional for a companion ('' = not chosen). */
  appearance: StorybookAppearance;
}

/** A photo prepared on the device (storybook-photo.ts). Ids p1..p6, unique within one request. */
export interface StorybookPhotoUpload {
  id: string;
  data: string;
  /**
   * Every other face in the photo (untagged, "not in the book", a friend, too
   * small), normalized like a face tag's box: the server blurs them out of
   * each stored crop. At most STORYBOOK_MAX_OTHER_FACES; left out when none.
   */
  others?: StorybookFaceBox[];
}

/** Who one face in one photo is. */
export interface StorybookFaceTag {
  photo: string;
  person: StorybookPersonId;
  box: StorybookFaceBox;
}

/** Photos and who is who in them: only photos with at least one tagged face are sent. */
export interface StorybookPhotoSet {
  photos: StorybookPhotoUpload[];
  faces: StorybookFaceTag[];
  /** The separate photo consent: required (true) whenever a photo is sent. */
  photoConsent: true;
  consentVersion?: string;
}

/** One grown-up drawn from a FaceLock avatar: `person` is 'c1' or 'c2' (an avatar is never the child's). */
export interface StorybookAvatarCast {
  person: 'c1' | 'c2';
  avatarId: string;
}

export interface CreateStorybookRequest {
  childName: string;
  age: number;
  gender: StorybookGender;
  language: StorybookLanguage;
  themeId: string;
  lessonId: string;
  artStyleId: string;
  interests: string;
  sidekick: string;
  dedication: string;
  appearance: StorybookAppearance;
  /** 0..config.cast.maxCompanions; companions[0] is c1, companions[1] is c2. */
  companions: StorybookCompanion[];
  photos?: StorybookPhotoUpload[];
  faces?: StorybookFaceTag[];
  /** The family pet, drawn from its kind and look (never a photo). */
  pet?: { name: string; kind: string; look: string };
  /** A grown-up of the cast drawn from the signed-in parent's FaceLock avatar (never also from photos). */
  avatars?: StorybookAvatarCast[];
  email: string;
  consent: true;
  /** The site mark on the digital PDF; the server keeps it on unless this is false. */
  watermark?: boolean;
  photoConsent?: true;
  consentVersion?: string;
}

/**
 * POST /books/:id/redraw: `{}` draws the cover again with a new seed; a photo
 * set first replaces the photos of every person tagged in it (the others keep
 * theirs).
 */
export type RedrawStorybookRequest = Record<string, never> | StorybookPhotoSet;

export interface CreateStorybookResponse {
  id: string;
  accessToken: string;
  status: StorybookStatus;
  gift?: 'applied' | 'not_applied';
}

// ---------------------------------------------------------------------------
// Gifts (a storybook paid for by someone else)
// ---------------------------------------------------------------------------

export type StorybookGiftStatus = 'pending' | 'paid' | 'redeeming' | 'redeemed' | 'refunded' | 'disputed';

export interface CreateStorybookGiftRequest {
  product: StorybookProduct;
  language: StorybookLanguage;
  fromName: string;
  fromEmail: string;
  toName: string;
  toEmail: string;
  childName: string;
  message: string;
}

export interface StorybookGift {
  id: string;
  status: StorybookGiftStatus;
  product: StorybookProduct;
  language: StorybookLanguage;
  fromName: string;
  toName: string;
  toEmail: string;
  childName: string;
  message: string;
  shipsTo: { city: string; country: string } | null;
  price: { currency: string; cents: number; usdCents: number };
}

export type StorybookProduct = 'pdf' | 'print';

/** A non-2xx answer (or no answer at all: status 0, code 'network_error'). */
export class StorybookApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field?: string;

  constructor(status: number, code: string, message?: string, field?: string) {
    super(message || code);
    this.name = 'StorybookApiError';
    this.status = status;
    this.code = code;
    this.field = field;
  }
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

async function readJson(response: Response): Promise<Record<string, unknown>> {
  try {
    const body = await response.json();
    return body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_ROOT}${path}`, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new StorybookApiError(0, 'network_error');
  }
  const body = await readJson(response);
  if (!response.ok) {
    const code = typeof body.error === 'string' && body.error ? body.error : `http_${response.status}`;
    const message = typeof body.message === 'string' ? body.message : undefined;
    const field = typeof body.field === 'string' ? body.field : undefined;
    throw new StorybookApiError(response.status, code, message, field);
  }
  return body as T;
}

function bookHeaders(token: string, json = false): Record<string, string> {
  const headers: Record<string, string> = { 'X-Storybook-Token': token };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

function readAuthToken(): string | null {
  try {
    return window.localStorage.getItem('auth_token');
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function getStorybookConfig(signal?: AbortSignal): Promise<StorybookConfig> {
  return request<StorybookConfig>('/config', { signal });
}

export function createStorybook(payload: CreateStorybookRequest): Promise<CreateStorybookResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Optional: a signed-in customer's books are linked to their account.
  const auth = readAuthToken();
  if (auth) headers.Authorization = `Bearer ${auth}`;
  return request<CreateStorybookResponse>('/books', {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
}

export function createStorybookGift(payload: CreateStorybookGiftRequest): Promise<{ id: string; code: string; url: string }> {
  return request<{ id: string; code: string; url: string }>('/gifts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function lookupStorybookGift(code: string, signal?: AbortSignal): Promise<StorybookGift> {
  return request<StorybookGift>('/gifts/lookup', { headers: { 'X-Gift-Code': code }, signal });
}

/** The giver's gift code, kept on this device for the "share the link" step after paying. */
export const storybookGiftKey = (id: string) => `storybook:gift:${id}`;

export function saveStorybookGiftCode(id: string, code: string): void {
  try {
    window.localStorage.setItem(storybookGiftKey(id), code);
  } catch {
    /* private mode: the giver still gets the link by email */
  }
}

export function readStorybookGiftCode(id: string): string | null {
  try {
    return window.localStorage.getItem(storybookGiftKey(id));
  } catch {
    return null;
  }
}

export function storybookGiftLink(code: string): string {
  return `${window.location.origin}/storybook/gift/redeem?code=${encodeURIComponent(code)}`;
}

export function getStorybook(id: string, token: string, signal?: AbortSignal): Promise<StorybookBook> {
  return request<StorybookBook>(`/books/${encodeURIComponent(id)}`, {
    headers: bookHeaders(token),
    signal,
  });
}

export function startStorybookCheckout(
  id: string,
  token: string,
  product: StorybookProduct,
): Promise<{ url: string }> {
  return request<{ url: string }>(`/books/${encodeURIComponent(id)}/checkout`, {
    method: 'POST',
    headers: bookHeaders(token, true),
    body: JSON.stringify({ product }),
  });
}

export function retryStorybook(id: string, token: string): Promise<{ status: StorybookStatus }> {
  return request<{ status: StorybookStatus }>(`/books/${encodeURIComponent(id)}/retry`, {
    method: 'POST',
    headers: bookHeaders(token, true),
    body: '{}',
  });
}

/** Draws the cover again (optionally from new photos). Answers with the book, now preview_generating. */
export function redrawStorybook(
  id: string,
  token: string,
  body: RedrawStorybookRequest = {},
): Promise<StorybookBook> {
  return request<StorybookBook>(`/books/${encodeURIComponent(id)}/redraw`, {
    method: 'POST',
    headers: bookHeaders(token, true),
    body: JSON.stringify(body),
  });
}

/**
 * Draws some pictures of a book in proofing again (one redraw round). The
 * book moves to `generating`; the page polls it from there, so the answer
 * (the book, or at least its status) is only a head start.
 */
export function proofRedraw(
  id: string,
  token: string,
  body: StorybookProofRedrawRequest,
): Promise<Partial<StorybookBook>> {
  return request<Partial<StorybookBook>>(`/books/${encodeURIComponent(id)}/proof/redraw`, {
    method: 'POST',
    headers: bookHeaders(token, true),
    body: JSON.stringify(body),
  });
}

/** Approves every picture of a book in proofing: only now is the book made (and printed). */
export function proofApprove(id: string, token: string): Promise<Partial<StorybookBook>> {
  return request<Partial<StorybookBook>>(`/books/${encodeURIComponent(id)}/proof/approve`, {
    method: 'POST',
    headers: bookHeaders(token, true),
    body: '{}',
  });
}

export function deleteStorybook(id: string, token: string): Promise<{ deleted: boolean }> {
  return request<{ deleted: boolean }>(`/books/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: bookHeaders(token),
  });
}

/**
 * Media URLs are signed, same-origin paths (/api/web/storybook/media/...).
 * When the frontend talks to a backend on another origin, prefix that origin.
 */
export function storybookMediaUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('/') && !url.startsWith('//') && BACKEND_BASE) return `${BACKEND_BASE}${url}`;
  return url;
}

// ---------------------------------------------------------------------------
// This device's books (localStorage). Only ids, dates and access tokens —
// never the child's name or anything else from the form.
// ---------------------------------------------------------------------------

export const STORYBOOK_BOOKS_KEY = 'storybook:books';
export const STORYBOOK_MAX_SAVED = 20;
export const storybookTokenKey = (id: string) => `storybook:token:${id}`;

export interface SavedStorybook {
  id: string;
  createdAt: string;
}

export function getStorybookToken(id: string): string | null {
  try {
    return window.localStorage.getItem(storybookTokenKey(id));
  } catch {
    return null;
  }
}

export function saveStorybookToken(id: string, token: string): void {
  try {
    window.localStorage.setItem(storybookTokenKey(id), token);
  } catch {
    /* storage blocked: the email link still opens the book */
  }
}

export function listSavedStorybooks(): SavedStorybook[] {
  try {
    const raw = window.localStorage.getItem(STORYBOOK_BOOKS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry): entry is SavedStorybook =>
        Boolean(entry) && typeof entry.id === 'string' && entry.id.length > 0)
      .map((entry) => ({ id: entry.id, createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : '' }))
      .slice(0, STORYBOOK_MAX_SAVED);
  } catch {
    return [];
  }
}

function writeSavedStorybooks(list: SavedStorybook[]): void {
  try {
    window.localStorage.setItem(STORYBOOK_BOOKS_KEY, JSON.stringify(list.slice(0, STORYBOOK_MAX_SAVED)));
  } catch {
    /* storage blocked */
  }
}

/** Newest first, de-duplicated, capped at 20. A known book keeps its original date. */
export function rememberStorybook(id: string, createdAt: string): void {
  const list = listSavedStorybooks();
  const existing = list.find((entry) => entry.id === id);
  if (existing) {
    if (!existing.createdAt && createdAt) {
      existing.createdAt = createdAt;
      writeSavedStorybooks(list);
    }
    return;
  }
  writeSavedStorybooks([{ id, createdAt }, ...list]);
}

/** Drops a deleted book from this device: its token and its list entry. */
export function forgetStorybook(id: string): void {
  writeSavedStorybooks(listSavedStorybooks().filter((entry) => entry.id !== id));
  try {
    window.localStorage.removeItem(storybookTokenKey(id));
  } catch {
    /* storage blocked */
  }
}

/**
 * What this tab is paying for, kept across the trip to Stripe (sessionStorage:
 * this tab only). Back with ?checkout=success it tells a print upgrade bought
 * while the book is still drawn (watched until the print lands) from a PDF
 * payment that already landed (nothing left to watch).
 */
export const storybookCheckoutKey = (id: string) => `storybook:checkout:${id}`;

export function stashStorybookCheckout(id: string, product: StorybookProduct): void {
  try {
    window.sessionStorage.setItem(storybookCheckoutKey(id), product);
  } catch {
    /* storage blocked: only the unambiguous payments are watched */
  }
}

/** Reads and forgets it: one checkout, one answer. */
export function takeStorybookCheckout(id: string): StorybookProduct | null {
  try {
    const value = window.sessionStorage.getItem(storybookCheckoutKey(id));
    window.sessionStorage.removeItem(storybookCheckoutKey(id));
    return value === 'pdf' || value === 'print' ? value : null;
  } catch {
    return null;
  }
}

/**
 * index.html moves ?k=<token> out of the address bar before any analytics
 * snippet runs and writes it straight to localStorage. Only when storage is
 * blocked does it park the token on window instead; this reads that copy.
 */
export function readStashedStorybookToken(id: string): string | null {
  const stash = (window as Window & { __storybookToken?: { id?: string; k?: string } }).__storybookToken;
  return stash && stash.id === id && typeof stash.k === 'string' && stash.k ? stash.k : null;
}
